import React, { useEffect, useState } from 'react';
import { mediaUrl } from '../../utils/mediaUrl';
import { AdminSidebar } from './AdminDashboardPage';
import AdminHeader from './AdminHeader';
import {
  listSizeCharts,
  createSizeChart,
  updateSizeChart,
  deleteSizeChart
} from '../../services/discoveryService';
import { uploadImage } from '../../services/productService';
import '../../css/AdminSizeChartsPage.css';
import {
  Plus,
  Ruler,
  Trash2,
  Edit3,
  Check,
  AlertCircle,
  UploadCloud,
  X,
  Loader2,
  AlertTriangle
} from 'lucide-react';

const SUBCATEGORIES = ['Saree', 'Kurti', 'Churidar Set', 'Short Tops', 'T-Shirt', 'Bottoms', 'Nightwear'];
const DEPARTMENTS = ['WOMEN', 'KIDS'];

const blank = () => ({
  category: '',
  subCategory: '',
  title: '',
  unit: 'in',
  columns: ['Bust', 'Waist', 'Hip'],
  rows: [{ size: 'S', values: ['', '', ''] }],
  howToMeasure: '',
  note: '',
  image: '',
  isActive: true
});

const chartLabel = (c) => c.subCategory || (c.category === 'KIDS' ? 'All Kids' : c.category ? `All ${c.category.toLowerCase()}` : 'Custom Chart');

// Size charts per category: shown in the product page "Size guide".
// Built-in defaults are used until custom brand versions are created.
const AdminSizeChartsPage = () => {
  const [charts, setCharts] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  // Modern In-App Delete Modal State
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    chart: null,
    loading: false
  });

  const load = async () => {
    try {
      const data = await listSizeCharts();
      setCharts(data.charts || []);
      setDefaults(data.defaults || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Could not load size charts');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const edit = (c) => {
    setError('');
    setMsg('');
    setEditing(JSON.parse(JSON.stringify({ ...blank(), ...c, isDefault: undefined })));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const set = (field, value) => setEditing((e) => ({ ...e, [field]: value }));

  const setColumn = (i, value) =>
    setEditing((e) => ({ ...e, columns: e.columns.map((c, j) => (j === i ? value : c)) }));
  
  const addColumn = () =>
    setEditing((e) => ({ ...e, columns: [...e.columns, ''], rows: e.rows.map((r) => ({ ...r, values: [...r.values, ''] })) }));
  
  const removeColumn = (i) =>
    setEditing((e) => ({
      ...e,
      columns: e.columns.filter((_, j) => j !== i),
      rows: e.rows.map((r) => ({ ...r, values: r.values.filter((_, j) => j !== i) }))
    }));

  const setCell = (ri, ci, value) =>
    setEditing((e) => ({
      ...e,
      rows: e.rows.map((r, j) =>
        j !== ri ? r : ci === -1 ? { ...r, size: value } : { ...r, values: r.values.map((v, k) => (k === ci ? value : v)) }
      )
    }));
  
  const addRow = () => setEditing((e) => ({ ...e, rows: [...e.rows, { size: '', values: e.columns.map(() => '') }] }));
  const removeRow = (i) => setEditing((e) => ({ ...e, rows: e.rows.filter((_, j) => j !== i) }));

  const onImage = async (file) => {
    if (!file) return;
    try {
      setUploadingImg(true);
      const url = await uploadImage(file);
      if (url) {
        set('image', url);
        setMsg('Image uploaded successfully');
      }
    } catch (e) {
      setError('Image upload failed');
    } finally {
      setUploadingImg(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = { ...editing };
      if (editing._id) await updateSizeChart(editing._id, payload);
      else await createSizeChart(payload);
      setMsg(`Saved size chart for ${chartLabel(editing)}`);
      setEditing(null);
      load();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not save size chart');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (c) => {
    setDeleteModal({ isOpen: true, chart: c, loading: false });
  };

  const confirmDelete = async () => {
    if (!deleteModal.chart) return;
    try {
      setDeleteModal((prev) => ({ ...prev, loading: true }));
      await deleteSizeChart(deleteModal.chart._id);
      setMsg(`Deleted ${chartLabel(deleteModal.chart)} chart. Default size chart restored.`);
      setDeleteModal({ isOpen: false, chart: null, loading: false });
      load();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to delete size chart');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <AdminHeader title="Size Charts" />

        <div className="asc-page">

        <div className="asc-head">
          <div className="asc-head-title">
            <h1>Size Charts</h1>
            <p>
              Configure measurement size guides shown to customers on product pages. Category-specific charts automatically take precedence over general department charts.
            </p>
          </div>
          {!editing && (
            <button className="asc-btn-primary" onClick={() => edit(blank())}>
              <Plus size={16} /> Add Size Chart
            </button>
          )}
        </div>

        {msg && (
          <div className="asc-status-banner good">
            <Check size={16} />
            <span>{msg}</span>
          </div>
        )}

        {error && (
          <div className="asc-status-banner bad">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* ---------- In-place Size Chart Editor ---------- */}
        {editing && (
          <div className="asc-editor-card">
            <div className="asc-editor-header">
              <h2>{editing._id ? `Edit: ${chartLabel(editing)}` : 'Create New Size Chart'}</h2>
              <button type="button" className="asc-btn-secondary" onClick={() => setEditing(null)}>
                <X size={15} /> Close
              </button>
            </div>

            <div className="asc-form-grid">
              <div className="asc-form-field">
                <label>Category (Specific Product Type)</label>
                <select value={editing.subCategory} onChange={(e) => set('subCategory', e.target.value)}>
                  <option value="">— Entire department —</option>
                  {SUBCATEGORIES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="asc-form-field">
                <label>Department {editing.subCategory ? '(Optional)' : ''}</label>
                <select value={editing.category} onChange={(e) => set('category', e.target.value)}>
                  <option value="">— All Departments —</option>
                  {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              <div className="asc-form-field">
                <label>Title Displayed to Customers</label>
                <input value={editing.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Kurtis, Churidar Sets" />
              </div>

              <div className="asc-form-field">
                <label>Measurement Unit</label>
                <select value={editing.unit} onChange={(e) => set('unit', e.target.value)}>
                  <option value="in">Inches (in)</option>
                  <option value="cm">Centimetres (cm)</option>
                </select>
              </div>
            </div>

            <div className="asc-table-wrap">
              <table className="asc-table">
                <thead>
                  <tr>
                    <th style={{ width: '100px' }}>Size</th>
                    {editing.columns.map((c, i) => (
                      <th key={i}>
                        <div className="asc-th-col">
                          <input
                            className="asc-th-input"
                            value={c}
                            onChange={(e) => setColumn(i, e.target.value)}
                            placeholder="Measurement"
                            aria-label={`Column ${i + 1} name`}
                          />
                          <button
                            type="button"
                            className="asc-col-del"
                            onClick={() => removeColumn(i)}
                            title={`Remove column ${c}`}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      </th>
                    ))}
                    <th style={{ width: '120px' }}>
                      {editing.columns.length < 8 && (
                        <button type="button" className="asc-btn-outline asc-btn-sm" onClick={addColumn}>
                          <Plus size={12} /> Column
                        </button>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {editing.rows.map((r, ri) => (
                    <tr key={ri}>
                      <td style={{ width: '100px' }}>
                        <input
                          value={r.size}
                          onChange={(e) => setCell(ri, -1, e.target.value)}
                          placeholder="S"
                          aria-label={`Row ${ri + 1} size`}
                        />
                      </td>
                      {editing.columns.map((_, ci) => (
                        <td key={ci}>
                          <input
                            value={r.values[ci] || ''}
                            onChange={(e) => setCell(ri, ci, e.target.value)}
                            placeholder="0"
                            aria-label={`${r.size || 'row'} ${editing.columns[ci]}`}
                          />
                        </td>
                      ))}
                      <td className="asc-td-action" style={{ width: '120px' }}>
                        <button type="button" className="asc-btn-danger-outline asc-btn-sm" onClick={() => removeRow(ri)}>
                          <Trash2 size={12} /> Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="asc-table-actions">
                <button type="button" className="asc-btn-secondary" onClick={addRow}>
                  <Plus size={14} /> Add Size Row
                </button>
                <span className="asc-table-count">
                  {editing.rows.length} size{editing.rows.length > 1 ? 's' : ''} configured
                </span>
              </div>
            </div>

            <div className="asc-form-grid asc-form-grid--notes">
              <div className="asc-form-field" style={{ gridColumn: 'span 2' }}>
                <label>How to Measure Guide (Optional instructions for shoppers)</label>
                <textarea
                  rows={2}
                  value={editing.howToMeasure}
                  placeholder="e.g. Bust: Measure across the fullest part. Waist: Measure around the natural waistline."
                  onChange={(e) => set('howToMeasure', e.target.value)}
                />
              </div>

              <div className="asc-form-field" style={{ gridColumn: 'span 2' }}>
                <label>Measurement Note (Optional footer note)</label>
                <input
                  value={editing.note}
                  onChange={(e) => set('note', e.target.value)}
                  placeholder="e.g. Garment length is measured from the high point of shoulder"
                />
              </div>

              <div className="asc-form-field" style={{ gridColumn: '1 / -1' }}>
                <label>Or Use Image Guide Instead of Table (Optional)</label>
                <div className="asc-img-uploader">
                  <label className="asc-btn-secondary" style={{ cursor: uploadingImg ? 'wait' : 'pointer' }}>
                    {uploadingImg ? (
                      <>
                        <Loader2 size={15} className="asc-spin" />
                        <span>Uploading Image...</span>
                      </>
                    ) : (
                      <>
                        <UploadCloud size={15} />
                        <span>Choose Size Chart Image</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      disabled={uploadingImg}
                      onChange={(e) => onImage(e.target.files?.[0])}
                      style={{ display: 'none' }}
                    />
                  </label>

                  {editing.image && (
                    <div className="asc-img-preview">
                      <img src={mediaUrl(editing.image)} alt="Size chart preview" />
                      <button type="button" className="asc-btn-danger-outline" onClick={() => set('image', '')}>
                        <X size={12} /> Remove Image
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="asc-editor-actions">
              <button type="button" className="asc-btn-secondary" onClick={() => setEditing(null)} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="asc-btn-primary" onClick={save} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 size={15} className="asc-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  'Save Chart'
                )}
              </button>
            </div>
          </div>
        )}

        {/* ---------- Section 1: Your Brand Charts ---------- */}
        <div className="asc-section-header">
          <h2>Your Custom Charts</h2>
          <p>Tailored brand measurement charts created specifically for your store.</p>
        </div>

        {charts.length === 0 ? (
          <div className="asc-empty-card">
            <div className="asc-empty-icon">
              <Ruler size={26} />
            </div>
            <h3>No Custom Size Charts Yet</h3>
            <p>
              Your catalog is currently utilizing Mithralaya's built-in standard measurements. Customize one below or click create to tailor dimensions for your brand.
            </p>
            <button className="asc-btn-primary" onClick={() => edit(blank())}>
              <Plus size={15} /> Create Brand Size Chart
            </button>
          </div>
        ) : (
          <div className="asc-chart-grid">
            {charts.map((c) => (
              <div className="asc-chart-card" key={c._id}>
                <div>
                  <div className="asc-card-top">
                    <div className="asc-card-category">
                      <div className="asc-card-badge-icon">
                        <Ruler size={18} />
                      </div>
                      <div className="asc-card-title">
                        <h3>{chartLabel(c)}</h3>
                        <span>{c.title || 'Brand Guide'}</span>
                      </div>
                    </div>
                    <span className="asc-tag-custom">Custom</span>
                  </div>

                  <div className="asc-card-body">
                    <div className="asc-card-metric">
                      {c.image ? 'Custom Infographic Image Guide' : `${c.rows?.length || 0} sizes (${c.rows?.[0]?.size || 'S'} - ${c.rows?.[c.rows.length - 1]?.size || 'XXL'})`}
                    </div>
                    {!c.image && (
                      <div className="asc-meas-chips">
                        {(c.columns || []).map((col) => (
                          <span className="asc-meas-chip" key={col}>
                            {col}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="asc-card-actions">
                  <button className="asc-btn-secondary" onClick={() => edit(c)}>
                    <Edit3 size={13} /> Edit
                  </button>
                  <button className="asc-btn-danger-outline" onClick={() => openDeleteModal(c)}>
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ---------- Section 2: Built-in Standard Charts ---------- */}
        {defaults.length > 0 && (
          <>
            <div className="asc-section-header">
              <h2>Built-in Charts in Use</h2>
              <p>Standard measurements. Click customize on any chart to replace it with your brand's specific dimensions.</p>
            </div>

            <div className="asc-chart-grid">
              {defaults.map((c) => {
                const label = chartLabel(c);
                return (
                  <div className="asc-chart-card" key={`${c.category}|${c.subCategory}`}>
                    <div>
                      <div className="asc-card-top">
                        <div className="asc-card-category">
                          <div className="asc-card-badge-icon">
                            <Ruler size={18} />
                          </div>
                          <div className="asc-card-title">
                            <h3>{label}</h3>
                            <span>{c.title || `${label} Guide`}</span>
                          </div>
                        </div>
                      </div>

                      <div className="asc-card-body">
                        <div className="asc-card-metric">
                          {c.rows?.length || 0} sizes ({c.rows?.[0]?.size || 'S'} – {c.rows?.[c.rows.length - 1]?.size || 'XXL'}) • {c.unit === 'cm' ? 'Centimetres' : 'Inches'}
                        </div>
                        <div className="asc-meas-chips">
                          {(c.columns || []).map((col) => (
                            <span className="asc-meas-chip" key={col}>
                              {col}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="asc-card-actions">
                      <button className="asc-btn-outline" onClick={() => edit(c)}>
                        <Edit3 size={13} /> Customize
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ---------- Delete Confirmation Modal ---------- */}
        {deleteModal.isOpen && (
          <div className="asc-modal-overlay" onClick={() => !deleteModal.loading && setDeleteModal({ isOpen: false, chart: null, loading: false })}>
            <div className="asc-modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="asc-modal-head">
                <h3>Delete Size Chart?</h3>
              </div>
              <div className="asc-modal-body">
                <div className="asc-modal-icon">
                  <AlertTriangle size={18} />
                </div>
                <p>
                  Are you sure you want to delete the customized <strong>{chartLabel(deleteModal.chart)}</strong> size chart? Products will automatically revert to using the standard built-in guide.
                </p>
              </div>
              <div className="asc-modal-actions">
                <button
                  type="button"
                  className="asc-btn-secondary"
                  disabled={deleteModal.loading}
                  onClick={() => setDeleteModal({ isOpen: false, chart: null, loading: false })}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="asc-btn-danger"
                  disabled={deleteModal.loading}
                  onClick={confirmDelete}
                >
                  {deleteModal.loading ? 'Deleting...' : 'Confirm Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default AdminSizeChartsPage;
